/**
 * The "Totals Breakdown by Tax Rate" block at the foot of the payment-report
 * Excel export (PaymentReportTableRow's handleExportReport). Kept separate
 * from the component, like paymentReportColumns.ts, so the columns and their
 * alignment can be unit tested without rendering or mocking XLSX.
 *
 * One row per tax rate: what the partner is owed before the additional fees,
 * each fee that comes off it, and what is left. The fees are the three the
 * payment run deducts — pickup, availability and barcode printing — and the
 * API sends each as a net amount keyed by the rate's percentage ("16"),
 * negative for a charge (a barcode printing credit, from a reversal settled
 * in a later period than its print, is positive). "After fees" is computed by
 * the API from all three, so a fee with no column here would lower that
 * figure with nothing on the sheet naming it.
 */
export const TAX_BREAKDOWN_TITLE = 'Totals Breakdown by Tax Rate'

export const TAX_BREAKDOWN_HEADERS = [
  'Tax Rate (%)',
  'Net Payable (Excl. Tax, Before Add. Fees)',
  'Pickup Fees Net',
  'Availability Fees Net',
  'Barcode Printing Fees Net',
  'Net Payable (Excl. Tax, After Add. Fees)',
  'Tax Value',
  'Net Payable (Incl. Tax, After Add. Fees)',
]

const EXCL_AFTER_COLUMN = TAX_BREAKDOWN_HEADERS.indexOf('Net Payable (Excl. Tax, After Add. Fees)')
const INCL_AFTER_COLUMN = TAX_BREAKDOWN_HEADERS.indexOf('Net Payable (Incl. Tax, After Add. Fees)')

/** The fee maps of the API's `totals`, in column order. */
const FEE_FIELDS = [
  'pickup_fees_net_by_tax_rate',
  'availability_fees_net_by_tax_rate',
  'barcode_printing_fees_net_by_tax_rate',
]

type RateMap = Record<string, number> | undefined | null

const amount = (map: RateMap, rate: string): number => Number(map?.[rate] ?? 0) || 0

/** A total as the API sent it, number or numeric string; a dash when it sent none. */
const money = (value: unknown): string =>
  value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
    ? '-'
    : Number(value).toFixed(2)

/**
 * The rates to print: every rate the partner sold at, plus any rate a fee was
 * charged at. A partner who sold only tax-exempt products still pays a fee at
 * the fee's own rate, and that row is the only place the fee can be shown.
 */
function breakdownRates(totals: any): string[] {
  const rates = new Set<string>(Object.keys(totals.gross_sales_exclude_tax_by_tax_rate))
  FEE_FIELDS.forEach((field) => {
    Object.keys(totals[field] ?? {}).forEach((rate) => {
      if (amount(totals[field], rate) !== 0) {
        rates.add(rate)
      }
    })
  })
  return Array.from(rates).sort((a, b) => parseFloat(a) - parseFloat(b))
}

/** A row as wide as the header, empty but for the cells given. */
function rowWith(cells: Record<number, string>): string[] {
  const row = TAX_BREAKDOWN_HEADERS.map(() => '')
  Object.entries(cells).forEach(([index, value]) => {
    row[Number(index)] = value
  })
  return row
}

/**
 * The whole block as rows for XLSX.utils.aoa_to_sheet: a spacer, the title,
 * the header, one row per rate, a spacer, and the two totals — each total
 * sits under the column it sums, with its label in the cell before it.
 */
export function buildTaxBreakdownRows(totals: any): string[][] {
  const rows: string[][] = [[], [TAX_BREAKDOWN_TITLE], [...TAX_BREAKDOWN_HEADERS]]

  if (totals?.gross_sales_exclude_tax_by_tax_rate) {
    breakdownRates(totals).forEach((rate) => {
      rows.push([
        `${parseFloat(rate)}%`,
        amount(totals.net_payable_exclude_tax_by_tax_rate, rate).toFixed(2),
        ...FEE_FIELDS.map((field) => amount(totals[field], rate).toFixed(2)),
        amount(totals.net_payable_exclude_tax_by_tax_rate_with_decrease_fees_total, rate).toFixed(
          2
        ),
        amount(totals.tax_val, rate).toFixed(2),
        amount(totals.net_payable_inclusive_tax_by_tax_rate_decrease_fees_total, rate).toFixed(2),
      ])
    })
  } else {
    rows.push(rowWith({0: 'No tax breakdown data available.'}))
  }

  rows.push([])
  rows.push(
    rowWith({
      [EXCL_AFTER_COLUMN - 1]: 'Net Payable To Partner (After Additional Fees, Excl. Tax)',
      [EXCL_AFTER_COLUMN]: money(totals?.net_payable_to_partner_after_fees_exclude_tax),
    })
  )
  rows.push(
    rowWith({
      [INCL_AFTER_COLUMN - 1]: 'Net Payable To Partner (Incl. Tax, After Add. Fees)',
      [INCL_AFTER_COLUMN]: money(totals?.net_payable_to_partner_after_fees_inclusive_tax),
    })
  )

  return rows
}
